import React from 'react';
import {
	FormattedMessage,
	defineMessages,
} from 'react-intl';

import Button, {
	ButtonType,
	ButtonWidth,
} from '~/components/patterns/buttons/Button';

import AngleDoubleLeftIconBuilder from '~/components/patterns/icons/builders/iconsData/navigationIcon/AngleDoubleLeft';
import AngleDoubleRightIconBuilder from '~/components/patterns/icons/builders/iconsData/navigationIcon/AngleDoubleRight';
import useMenuExpandedState from '~/hooks/useMenuExpandedState';
import useViewportType from '~/hooks/useViewportType';
import {
	isPhase3,
} from '~/utilities/viewportSize';



const messages = defineMessages({
	minimize: {
		id: 'ui.navigation.button.minimize',
		defaultMessage: 'Minimize',
	},
	keepOpen: {
		id: 'ui.navigation.button.keepOpen',
		defaultMessage: 'Keep open',
	},
});

/**
 * Angle Double Left Icon - from cond-ui-kit
 * Used for "Minimize" state (when navbar is expanded/Phase 3)
 */
const AngleDoubleLeftIcon: React.FC<{ color?: string }> = ({ color = '#89c32e' }) => {
	const iconBuilder = AngleDoubleLeftIconBuilder;
	const icon = iconBuilder.icon({ color, size: 20 });

	return (
		<svg
			fill="none"
			height="20"
			viewBox={`0 0 ${iconBuilder.viewBoxWidth} ${iconBuilder.viewBoxHeight}`}
			width="20"
			xmlns="http://www.w3.org/2000/svg"
		>
			{icon}
		</svg>
	);
};

/**
 * Angle Double Right Icon - from cond-ui-kit
 * Used for "Keep open" state (when navbar is minimized/Phase 2)
 */
const AngleDoubleRightIcon: React.FC<{ color?: string }> = ({ color = '#89c32e' }) => {
	const iconBuilder = AngleDoubleRightIconBuilder;
	const icon = iconBuilder.icon({ color, size: 20 });

	return (
		<svg
			fill="none"
			height="20"
			viewBox={`0 0 ${iconBuilder.viewBoxWidth} ${iconBuilder.viewBoxHeight}`}
			width="20"
			xmlns="http://www.w3.org/2000/svg"
		>
			{icon}
		</svg>
	);
};

/**
 * Navigation Minimize Toggle Component
 *
 * Button that allows users to manually control navbar expansion state.
 *
 * Behavior:
 * - When navbar is expanded (Phase 3, either naturally or via menu expansion):
 *   - Shows "Minimize" label with left arrow icon
 *   - Clicking collapses to Phase 2 (only works if menu was expanded via click)
 * - When navbar is collapsed (Phase 2):
 *   - Shows "Keep open" label with right arrow icon (only visible in Phase 3)
 *   - Clicking expands to Phase 3
 *
 * Note: In naturally Phase 3 (viewport ≥1169px), the button allows minimizing
 * to Phase 2 even on large screens, giving users manual control.
 */
const NavigationMinimizeToggle: React.FC = () => {
	const viewportType = useViewportType();
	const [
		isMenuExpanded,
		setMenuExpanded,
		isMenuMinimized,
		setMenuMinimized,
	] = useMenuExpandedState();

	// Check if we're in Phase 3 (either naturally or via menu expansion)
	const effectivePhase3 = isPhase3(viewportType, isMenuExpanded, isMenuMinimized);
	const isNaturallyPhase3 = isPhase3(viewportType);

	// Determine button state based on effective phase
	const isExpanded = effectivePhase3;

	// Choose icon and text based on current state
	const buttonIcon = isExpanded
		? <AngleDoubleLeftIcon />
		: <AngleDoubleRightIcon />;
	const buttonText = isExpanded
		? <FormattedMessage {...messages.minimize} />
		: <FormattedMessage {...messages.keepOpen} />;

	const handleClick = React.useCallback(
		(e: React.MouseEvent) => {
			e.preventDefault();
			e.stopPropagation();

			if (isExpanded) {
				// Currently expanded: collapse to Phase 2
				// If naturally Phase 3, use minimized state; otherwise clear expansion
				if (isNaturallyPhase3) {
					setMenuMinimized(true);
				} else {
					setMenuExpanded(false);
				}
			} else {
				// Currently collapsed: expand to Phase 3
				// Clear minimized state and set expansion
				setMenuMinimized(false);
				setMenuExpanded(true);
			}
		},
		[isExpanded, isNaturallyPhase3, setMenuExpanded, setMenuMinimized],
	);

	return (
		<div
			className="navigation-minimize-toggle-wrapper"
			onClick={handleClick}
		>
			<span className="navigation-minimize-toggle-label">
				{buttonText}
			</span>
			<Button
				className="navigation-minimize-toggle-button"
				icon={buttonIcon}
				type={ButtonType.Button}
				width={ButtonWidth.Fullwidth}
			>
				{/* Empty children - label is rendered outside button */}
			</Button>
		</div>
	);
};

export default NavigationMinimizeToggle;
